import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import TaskList from './components/TaskList';
import AdminPanel from './components/AdminPanel';
import BoardList from './components/BoardList';
import BoardView from './components/BoardView';
import UsersList from './components/UsersList';
import UserDetails from './components/UserDetails';
import MyTasks from './components/MyTasks';
import TasksForReview from './components/TasksForReview';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (!isAdmin) {
    return <Navigate to="/" />;
  }
  return <>{children}</>;
};

const ManagerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, isManager } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (!isAdmin && !isManager) {
    return <Navigate to="/" />;
  }
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" /> : <Login />}
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <BoardList />
          </PrivateRoute>
        }
      />
      <Route
        path="/boards"
        element={
          <PrivateRoute>
            <BoardList />
          </PrivateRoute>
        }
      />
      <Route
        path="/board/:id"
        element={
          <PrivateRoute>
            <BoardView />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <PrivateRoute>
            <TaskList />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-tasks"
        element={
          <PrivateRoute>
            <MyTasks />
          </PrivateRoute>
        }
      />
      <Route
        path="/for-review"
        element={
          <PrivateRoute>
            <TasksForReview />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ManagerRoute>
            <UsersList />
          </ManagerRoute>
        }
      />
      <Route
        path="/users/:id"
        element={
          <ManagerRoute>
            <UserDetails />
          </ManagerRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
